/* eslint consistent-return: 0 */
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import InfoIcon from '@mui/icons-material/Info';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { UrlParams } from 'common/types';
import { KubeCluster, Sources } from 'common/graphqlTypes';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { EventCacheData } from 'hooks/useLRQ/useEventCache';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { pluralize } from 'common/util/string';

import { searchParamState } from 'components/common/Managers/RouteStateManager.state';
import OverviewFooter from 'components/overview/OverviewFooter';
import Button from 'components/common/Button/Button';

import LogVolumeGraph from './LogVolumeGraph';
import SourcesList from './SourcesList';
import ClustersList from './ClustersList';
import { filterClusters, filterHosts } from './LogOverviewPage.functions';
import { overviewFilterTextState } from './LogOverviewFilter';

import './LogOverviewSourcesTab.scss';

/**
 * Component for the log overview sources tab on the log overview page.
 * This tab includes a list of the sources and clusters setup for the user.
 */
type LogOverviewSourcesTabProps = {
  sources: Sources;
  results: EventCacheData[];
};

function LogOverviewSourcesTab({ sources, results }: LogOverviewSourcesTabProps): JSX.Element {
  const setSearchParams = useSetSearchParams();
  const showOld = useRecoilValue(searchParamState(K.URL_PARAM.SHOW_OLD));
  const filterText = useRecoilValue(overviewFilterTextState);
  const debouncedFilterText = useDebouncedValue(filterText, 250);

  type Host = {
    logFiles: string[];
  };

  const hosts = sources?.hosts || [];
  const logsCount = hosts.reduce((count: number, host: Host) => count + (host.logFiles && host.logFiles.length), 0);

  const kubeClusters = sources?.kubeClusters || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const controllerCount = kubeClusters.reduce(
    (count: number, cluster: any) => count + (cluster.controllers ? cluster.controllers.length : 0),
    0,
  );

  const filteredClusters = useMemo(
    () => filterClusters(kubeClusters, debouncedFilterText) as Array<KubeCluster>,
    [debouncedFilterText, kubeClusters],
  );
  const filteredHosts = useMemo(() => filterHosts(hosts, debouncedFilterText), [hosts, debouncedFilterText]);

  const getReceivingDataText = (): string => {
    const pluralizedClusters = pluralize('cluster', kubeClusters.length);
    const pluralizedDeployments = pluralize('deployment', controllerCount);
    const pluralizedHosts = pluralize('host', hosts.length);
    const pluralizedLogs = pluralize('log', logsCount);

    let receivingDataText = '';

    if (kubeClusters.length > 0 && hosts.length > 0) {
      receivingDataText = `Receiving data from ${kubeClusters.length} ${pluralizedClusters} with ${controllerCount} ${pluralizedDeployments}, ${hosts.length} ${pluralizedHosts} and ${logsCount} ${pluralizedLogs}`;
    } else if (kubeClusters.length > 0) {
      receivingDataText = `Receiving data from ${kubeClusters.length} ${pluralizedClusters} with ${controllerCount} ${pluralizedDeployments}`;
    } else if (hosts.length > 0) {
      receivingDataText = `Receiving data from ${hosts.length} ${pluralizedHosts} and ${logsCount} ${pluralizedLogs}`;
    }

    return receivingDataText;
  };

  const renderReceivingDataText = (): JSX.Element | null => {
    const receivingDataText = getReceivingDataText();
    return receivingDataText ? (
      <div className="LogOverviewSourcesTab__ReceivingDataText">{receivingDataText}</div>
    ) : null;
  };

  const onShowAllClick = (): void => {
    setSearchParams({
      [K.URL_PARAM.SHOW_OLD]: 'true',
    } as UrlParams);
  };

  const excludedHostCount = sources?.totalHosts ? sources.totalHosts - hosts.length : 0;
  const excludedLogCount = sources?.totalLogs ? sources.totalLogs - logsCount : 0;
  const hasExcludedLogs = excludedLogCount > 0;
  const hasExcludedHosts = excludedHostCount > 0;

  const shouldRenderShowAllLogs = !showOld && (hasExcludedLogs || hasExcludedHosts);

  const renderShowAllLogsText = (): JSX.Element | null => {
    const pluralizedExcludedHosts = pluralize('host', excludedHostCount);
    const pluralizedExcludedLogs = pluralize('log', excludedLogCount);

    let showOldLogsText = '';
    if (hasExcludedLogs && hasExcludedHosts) {
      showOldLogsText = `${excludedLogCount} ${pluralizedExcludedLogs} and ${excludedHostCount} ${pluralizedExcludedHosts} not shown because they have not sent any data for 24 hours`;
    } else if (hasExcludedLogs) {
      showOldLogsText = `${excludedLogCount} ${pluralizedExcludedLogs} not shown because they have not sent any data for 24 hours`;
    } else if (hasExcludedHosts) {
      showOldLogsText = `${excludedHostCount} ${pluralizedExcludedHosts} not shown because they have not sent any data for 24 hours`;
    }

    return shouldRenderShowAllLogs ? (
      <div className="LogOverviewSourcesTab__ShowOldLogsText">
        <InfoIcon className="LogOverviewSourcesTab__InfoIcon" htmlColor="#FFBB34" />
        {showOldLogsText}
        <Button
          className="LogOverviewSourcesTab__ShowAllButton"
          size="sm"
          onClick={onShowAllClick}
          testId={ELEMENT.overviewShowAll}
        >
          Show All
        </Button>
      </div>
    ) : null;
  };

  const renderLogVolumeGraph = (): JSX.Element | null => {
    const seriesData = results?.[0]?.seriesData?.series;
    return <LogVolumeGraph data={seriesData} />;
  };

  return (
    <div className="LogOverviewSourcesTab">
      {renderLogVolumeGraph()}
      {renderReceivingDataText()}
      {renderShowAllLogsText()}
      <ClustersList clusters={filteredClusters} />
      <SourcesList sources={filteredHosts} />
      <OverviewFooter />
    </div>
  );
}

export default LogOverviewSourcesTab;
